import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { Part } from '@reikraft/shared';
import { type Column, ErrorBox, Field, Icon, Loader, Modal, Notice, Table, useToast } from '@reikraft/ui';
import { api, errorText } from '../api';
import { FormMessage, PageHead, useFormErrors } from '../components/common';
import { formatMoney, kopToRub, rubToKop } from '../format';
import { useFlag } from '../session';

export function PartsPage() {
  const on = useFlag('inventory');
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = useState<Part | 'new' | null>(null);
  const [move, setMove] = useState<Part | null>(null);
  const [delta, setDelta] = useState('');
  const list = useQuery({ queryKey: ['parts'], queryFn: () => api.get<Part[]>('/admin/parts'), enabled: on });
  const doMove = useMutation({
    mutationFn: () => api.post<Part[]>(`/admin/parts/${move!.id}/move`, { delta: Number(delta), note: null }),
    onSuccess: (data) => {
      qc.setQueryData(['parts'], data);
      setMove(null);
      setDelta('');
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });
  if (!on)
    return (
      <Notice label="Раздел выключен">
        Включите «Склад запчастей» в настройках, чтобы вести остатки ремкомплектов и списывать их на заказы.
      </Notice>
    );
  const columns: Column<Part>[] = [
    { key: 'name', title: 'Деталь', width: 'minmax(200px, 1.6fr)', render: (p) => <b style={{ fontWeight: 600 }}>{p.name}</b> },
    { key: 'sku', title: 'Артикул', width: '120px', render: (p) => <span className="rk-mono">{p.sku ?? '—'}</span> },
    {
      key: 'qty',
      title: 'Остаток',
      width: '100px',
      render: (p) => (
        <b className="rk-mono" style={{ color: p.qty <= p.minQty ? 'var(--danger-text)' : undefined }}>
          {p.qty}
        </b>
      ),
    },
    { key: 'min', title: 'Мин.', width: '70px', render: (p) => <span className="rk-mono rk-muted">{p.minQty}</span> },
    {
      key: 'cost',
      title: 'Цена закупки',
      width: '120px',
      align: 'right',
      render: (p) => <span className="rk-mono">{formatMoney(p.costKop)}</span>,
    },
    {
      key: 'act',
      title: '',
      width: '120px',
      render: (p) => (
        <button
          type="button"
          className="rk-btn rk-btn--sm"
          onClick={(e) => {
            e.stopPropagation();
            setMove(p);
          }}
        >
          Приход / расход
        </button>
      ),
    },
  ];
  return (
    <>
      <PageHead
        title="Запчасти"
        sub="Ремкомплекты, сальники, втулки, пыльники"
        actions={
          <button type="button" className="rk-btn rk-btn--primary" onClick={() => setEdit('new')}>
            <Icon name="plus" size={16} /> Деталь
          </button>
        }
      />
      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Table
          label="Запчасти"
          columns={columns}
          rows={list.data!}
          rowKey={(p) => p.id}
          onRowClick={(p) => setEdit(p)}
          minWidth={720}
          empty="Деталей нет"
        />
      )}
      <PartModal part={edit} onClose={() => setEdit(null)} />
      <Modal
        open={!!move}
        onClose={() => setMove(null)}
        title={move ? `${move.name} · остаток ${move.qty}` : ''}
        footer={
          <button
            type="button"
            className="rk-btn rk-btn--primary"
            disabled={!Number(delta) || doMove.isPending}
            onClick={() => doMove.mutate()}
          >
            Записать
          </button>
        }
      >
        <Field label="Изменение, шт." hint="Приход — положительное число, списание — отрицательное">
          <input
            className="rk-input rk-input--mono"
            inputMode="numeric"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            autoFocus
          />
        </Field>
      </Modal>
    </>
  );
}

function PartModal({ part, onClose }: { part: Part | 'new' | null; onClose: () => void }) {
  const [f, setF] = useState({ name: '', sku: '', qty: '0', minQty: '0', cost: '' });
  const errs = useFormErrors();
  const qc = useQueryClient();
  useEffect(() => {
    if (part && part !== 'new')
      setF({ name: part.name, sku: part.sku ?? '', qty: String(part.qty), minQty: String(part.minQty), cost: kopToRub(part.costKop) });
    else if (part === 'new') setF({ name: '', sku: '', qty: '0', minQty: '0', cost: '' });
    errs.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [part]);
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name, sku: f.sku || null, qty: Number(f.qty) || 0, minQty: Number(f.minQty) || 0, costKop: rubToKop(f.cost) };
      return part && part !== 'new' ? api.put<Part[]>(`/admin/parts/${part.id}`, body) : api.post<Part[]>('/admin/parts', body);
    },
    onSuccess: (data) => {
      qc.setQueryData(['parts'], data);
      onClose();
    },
    onError: (e) => errs.set(e),
  });
  const bind = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  return (
    <Modal
      open={!!part}
      onClose={onClose}
      title={part === 'new' ? 'Новая деталь' : 'Деталь'}
      footer={
        <button type="button" className="rk-btn rk-btn--primary" disabled={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </button>
      }
    >
      <FormMessage message={errs.message} />
      <Field label="Название" error={errs.errors.name}>
        <input className="rk-input" {...bind('name')} />
      </Field>
      <div className="rk-grid-form">
        <Field label="Артикул">
          <input className="rk-input rk-input--mono" {...bind('sku')} />
        </Field>
        <Field label="Остаток">
          <input className="rk-input rk-input--mono" type="number" min={0} {...bind('qty')} />
        </Field>
        <Field label="Минимальный остаток">
          <input className="rk-input rk-input--mono" type="number" min={0} {...bind('minQty')} />
        </Field>
        <Field label="Цена закупки, ₽">
          <input className="rk-input rk-input--mono" inputMode="decimal" {...bind('cost')} />
        </Field>
      </div>
    </Modal>
  );
}
